import { reactive, toRaw } from 'vue';
import axios from 'axios';
import Helper from '@/libraries/Helpers';
import useTransport from '@/stores/Transport';

const httpInstance = axios.create();

const { setTransport } = useTransport();

const state = reactive({
  products: [],
  totalPrice: 0,
  totalPriceWithVat: 0
});

export default function useProduct() {
  const updateProduct = async (uID, amount = 1) => {
    try {
      const oldProducts = toRaw(state.products);
      const {
        data: { items, transport, success }
      } = await httpInstance.post('get/api/shopBasket/item', {
        uID,
        amount
      });
      setProducts(items);
      setTransport(transport);

      if (success) {
        const dataProducts = [];
        let totalPriceWithVat = 0;

        items.forEach((product, productIndex) => {
          if (product.uID === uID) {
            let oldQuantity = 0;
            const oldProduct = oldProducts.find(op => op.uID === product.uID);
            if (oldProduct) {
              oldQuantity = oldProduct.amount;
            }

            let quantity = Math.abs(product.amount - oldQuantity);

            const priceWithVat = Helper.roundPrice(
              Helper.vatPrice(product.price, product.vat)
            );
            let discountPriceWithVat = null;
            if (product.discountPrice) {
              discountPriceWithVat =
                priceWithVat -
                Helper.roundPrice(
                  Helper.vatPrice(product.discountPrice, product.vat)
                );
            }
            if (discountPriceWithVat !== null) {
              totalPriceWithVat += discountPriceWithVat * quantity;
            } else {
              totalPriceWithVat += priceWithVat * quantity;
            }

            let categoryName = '';

            if (product.categories.length > 0) {
              categoryName = product.categories[0].name;
            }

            dataProducts.push({
              index: productIndex,
              item_id: product.productCode || product.id,
              item_name: product.productName,
              item_brand: '',
              item_category: categoryName,
              price: priceWithVat,
              discount: discountPriceWithVat || 0,
              quantity: quantity
            });
          }
        });
        if (dataProducts.length > 0) {
          if (typeof window.dataLayer === 'undefined') {
            window.dataLayer = [];
          }
          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'EUR',
              value: totalPriceWithVat,
              items: dataProducts
            }
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const removeProduct = async uID => {
    try {
      const oldProducts = toRaw(state.products);

      const {
        data: { items, transport, success }
      } = await httpInstance.post('get/api/shopBasket/removeItem', {
        uID
      });
      setProducts(items);
      setTransport(transport);
      if (success) {
        const product = oldProducts.find(op => op.uID === uID);
        if (product) {
          let totalPriceWithVat = 0;
          const priceWithVat = Helper.roundPrice(
            Helper.vatPrice(product.price, product.vat)
          );
          let discountPriceWithVat = null;
          if (product.discountPrice) {
            discountPriceWithVat =
              priceWithVat -
              Helper.roundPrice(
                Helper.vatPrice(product.discountPrice, product.vat)
              );
          }
          if (discountPriceWithVat !== null) {
            totalPriceWithVat += discountPriceWithVat * product.amount;
          } else {
            totalPriceWithVat += priceWithVat * product.amount;
          }

          let categoryName = '';

          if (product.categories.length > 0) {
            categoryName = product.categories[0].name;
          }

          if (typeof window.dataLayer === 'undefined') {
            window.dataLayer = [];
          }

          window.dataLayer.push({ ecommerce: null });
          window.dataLayer.push({
            event: 'remove_from_cart',
            ecommerce: {
              currency: 'EUR',
              value: totalPriceWithVat,
              items: [
                {
                  currency: 'EUR',
                  value: totalPriceWithVat,
                  items: [
                    {
                      index: 0,
                      item_id: product.productCode || product.id,
                      item_name: product.productName,
                      item_brand: '',
                      item_category: categoryName,
                      price: priceWithVat,
                      discount: discountPriceWithVat || 0,
                      quantity: product.amount
                    }
                  ]
                }
              ]
            }
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const setProducts = products => {
    let totalPrice = 0;
    let totalPriceWithVat = 0;

    const collectProducts = [];
    products.forEach(product => {
      product.discountPriceWithVat = Helper.vatPrice(
        product.discountPrice,
        product.vat
      );
      product.priceWithVat = Helper.vatPrice(product.price, product.vat);

      product.totalDiscountPriceWithVat =
        Helper.roundPrice(Helper.vatPrice(product.discountPrice, product.vat)) *
        parseInt(product.amount);

      product.totalPriceWithVat =
        Helper.roundPrice(Helper.vatPrice(product.price, product.vat)) *
        parseInt(product.amount);

      totalPrice += parseFloat(product.price) * parseInt(product.amount);
      totalPriceWithVat +=
        product.totalDiscountPriceWithVat || product.totalPriceWithVat;

      collectProducts.push(product);
    });

    state.products = collectProducts;
    state.totalPrice = totalPrice;
    state.totalPriceWithVat = totalPriceWithVat;
  };

  const getProducts = () => {
    return state.products;
  };

  const getTotalPrice = () => {
    return state.totalPrice;
  };

  const getTotalPriceWithVat = () => {
    return state.totalPriceWithVat;
  };

  return {
    updateProduct,
    removeProduct,
    setProducts,
    getProducts,
    getTotalPrice,
    getTotalPriceWithVat
  };
}
